// Vendors
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Actions
import {
  setStep,
  verifyConsent,
  logoutAndSignInDifferent,
  AUTH_STEPS,
  AUTH_SERVER_ERROR_MODAL_OPEN,
} from '../../../reducers/auth/authActions';

// Selectors
import { selectClientId, selectAuthorizeResponse, selectAppUrl, selectUserDetails } from '../../../reducers/auth/authSelectors';
import { selectAppName } from '../../../reducers/auth/authSelectors';

// Helpers
import { clearAuthSession } from '../../../reducers/auth/authAuthorizeHelpers';

// Styles
import './continue-to.scss';

// Components
import { 
  TxButton,
  TxButtonMode,
  TxLoadingText,
} from 'texkit-ui';
import { Trans } from '../../../components/Trans';

const ContinueTo = () => {
  const dispatch = useDispatch();
  const clientId = useSelector(selectClientId);
  const authorizeResponse = useSelector(selectAuthorizeResponse);
  const appName = useSelector(selectAppName);
  const appUrl = useSelector(selectAppUrl);
  const userDetails = useSelector(selectUserDetails) || {};
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSignInDifferent, setIsLoadingSignInDifferent] = useState(false);
  
  const { firstname = '', lastname = '', userEmail = '' } = userDetails;
  const fullName = [firstname, lastname].filter(Boolean).join(' ');

  const handleContinue = async () => {
    if (isLoading) return;
    setIsLoading(true);  
    const customerAppClientId = clientId;
    const verifyConsentOutcome = await dispatch(verifyConsent({ customerAppClientId }));

    if (verifyConsentOutcome?.failed) {
      const uniqueIdentifier = verifyConsentOutcome?.uniqueIdentifier || null;
      dispatch({ type: AUTH_SERVER_ERROR_MODAL_OPEN, payload: { uniqueIdentifier } });
      setIsLoading(false);
      return;
    }

    if (!verifyConsentOutcome?.showGrantPermission) {
      const location = authorizeResponse?.location;
      if (location) {
        clearAuthSession();
        window.location.href = location;
      } else {
        setIsLoading(false);
      }
      return;
    }

    dispatch(setStep(AUTH_STEPS.GRANT_PERMISSION));
    setIsLoading(false);
  };

  const handleSignInDifferent = async () => {
    if (isLoadingSignInDifferent) return;
    setIsLoadingSignInDifferent(true);
    const logoutResult = await dispatch(logoutAndSignInDifferent());
    
    if (logoutResult?.success && appUrl) {
      clearAuthSession();
      window.location.href = appUrl;
    } else {
      setIsLoadingSignInDifferent(false);
    }
  };

  return (
    <div className="step continue-to-step">
      <h1 className="step-title continue-to-title">
        <Trans file="auth" id="continueToTitle" />{' '}
        {appName}
      </h1>
      <p className="step-subtitle continue-to-subtitle">
        <Trans file="auth" id="signedInAs" />
      </p>

      <div className="continue-to-user">
        <div className="continue-to-info">
          <span className="continue-to-name">{fullName}</span>
          <span className="continue-to-email">{userEmail}</span>
        </div>
      </div>

      <TxButton
        className="continue-to-btn"
        mode={TxButtonMode.Primary}
        onClick={handleContinue}
        disabled={isLoading || isLoadingSignInDifferent}
        aria-busy={isLoading}
      >
        {isLoading ? (
          <TxLoadingText>
            <Trans file="auth" id="signInNext" />
          </TxLoadingText>
        ) : (
          <Trans file="auth" id="signInNext" />
        )}
      </TxButton>

      <div className="continue-to-different-wrapper">
        <span className="continue-to-different-text">
          <Trans file="auth" id="notYouText" />{' '}
        </span>
        <TxButton 
          className="continue-to-different-link" 
          type="button" 
          onClick={handleSignInDifferent}
          disabled={isLoading || isLoadingSignInDifferent}
        >
          <Trans file="auth" id="signInDifferentLink" />
        </TxButton>
      </div>
    </div>
  );
};

export default ContinueTo;
